import { Injectable } from '@angular/core';
import * as tj from '@mapbox/togeojson';
import { Store } from '@ngrx/store';
import { FeatureCollection, Geometry } from 'geojson';
import JSZip from 'jszip';
import tokml from 'geojson-to-kml';
import { cloneDeep } from 'lodash';

import { Document, Site } from '@shared/models';
import * as fromApp from '@store';
import { setForestryKml } from '@store/parcel/parcel.actions';
import { DocumentService } from '@core/api/document.service';
import { ToastService } from '@shared/services/toast.service';
import { ToastLevel } from '@shared/models/toast-message.module';
import { mergeMap, take } from 'rxjs/operators';
import { SpinnerService } from '@shared/spinner/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class KmlForestryUploadService {
  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly documentService: DocumentService,
    readonly spinnerService: SpinnerService,
    readonly toastService: ToastService
  ) {}

  parseKmlDocument(file, setStore = true): Promise<FeatureCollection<Geometry>> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = (e: any) => {
        try {
          resolve(this.extractAndParseKML(e.target.result, setStore));
        } catch (error) {
          reject(new Error('error converting the KML file ' + error.message));
        }
      };
      fileReader.readAsText(file);
    });
  }

  extractAndParseKML(fileData, setStore = true) {
    const geoJSONFromKML: FeatureCollection = this.parseKMLContent(fileData);
    if (setStore) {
      this.store.dispatch(setForestryKml({ data: geoJSONFromKML.features }));
    }
    return geoJSONFromKML;
  }

  parseKmzDocument(file, setStore = true): Promise<FeatureCollection<Geometry>> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = async (e: any) => {
        try {
          const zipParser = new JSZip();
          zipParser.loadAsync(e.target.result).then(
            zip => {
              const kmlFile = Object.values(zip.files).filter(doc => doc.name.toLowerCase().endsWith('.kml'))[0];
              kmlFile.async('text').then(content => {
                resolve(this.extractAndParseKML(content, setStore))
              });
            });
        } catch (error) {
          reject(new Error('error converting the KMZ file ' + error.message));
        }
      }
      fileReader.readAsArrayBuffer(file);
    });
  }

  parseKMLContent = (content: string): FeatureCollection => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, 'text/xml');
    if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
      this.toastService.sendMessage('XML is Invalid. Please correct before continuing.', ToastLevel.WARN);
      throw new Error('Invalid XML found!');
    }
    return tj.kml(xmlDoc);
  };

  uploadKMLToSite(site: Site, kmlFile: File) {
    const newDoc = {
      site_id: site.id,
      document_type_id: this.documentService.getDocTypeId('Site KML'),
      name: new Date().getTime() + '_' + kmlFile.name,
    };
    this.documentService.uploadAndCreateDocument(site, kmlFile, newDoc);
  }

  createKmlForSiteStands(site: Site) {
    const arrayOfFeatures = [];
    site.versions.forEach(version => {
      return version.stands.forEach(stand => {
        const clonedStand = cloneDeep(stand);
        clonedStand.feature.valuation = null;
        clonedStand.feature.parcel_id = null;
        arrayOfFeatures.push(clonedStand.feature);
      });
    });

    const geoJSONFile = {
      type: 'FeatureCollection',
      features: arrayOfFeatures,
    };
    const kml = tokml(geoJSONFile);

    return this.documentService
      .uploadFile(site.identifier, kml.toString())
      .pipe(
        take(1),
        mergeMap(fileKey => {
          const newDoc = new Document({
            siteId: site.id,
            documentTypeId: this.documentService.getDocTypeId('Site KML'),
            name: `${new Date().getTime()}_definition.kml`,
            identifier: fileKey,
          });
          return this.documentService.createDocument(newDoc);
        }),
      )
  }
}
